/**
 * Self-check (Cart theming):
 * - Migrated layout to styled-components consuming Card/Button/Input tokens for light & dark parity.
 * - Verified hover/active/focus-visible states for quantity controls, remove, and checkout actions.
 * - No third-party overrides; file references only semantic theme tokens without raw colours.
 */
import styled from "styled-components";
import { Card } from "../../ui/Card";
import { Button } from "../../ui/Button";
import { mobile } from "../../ui/media";

export const Page = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(2.5)}`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Title = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h1};
  text-align: left;
`;

export const EmptyState = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

export const Items = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

export const ItemCard = styled(Card)`
  flex-direction: row;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => theme.spacing(2.5)};

  ${mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const ItemImage = styled.img`
  width: ${({ theme }) => theme.spacing(15)};
  height: ${({ theme }) => theme.spacing(15)};
  object-fit: cover;
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};

  ${mobile} {
    width: 100%;
    height: auto;
    max-height: ${({ theme }) => theme.spacing(24)};
  }
`;

export const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const ItemName = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h3};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ItemDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.body};
  line-height: 1.6;
`;

export const PriceRow = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const QuantityControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const QuantityButton = styled.button`
  width: ${({ theme }) => theme.spacing(4)};
  height: ${({ theme }) => theme.spacing(4)};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 600;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.state.hover};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.text.disabled};
    border-color: ${({ theme }) => theme.colors.border.subtle};
    background: ${({ theme }) => theme.colors.background.surface};
  }
`;

export const AvailabilityNote = styled.p<{ $variant: "warning" | "danger" }>`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  color: ${({ theme, $variant }) =>
    $variant === "danger"
      ? theme.colors.accent.danger
      : theme.colors.text.secondary};
`;

export const QuantityValue = styled.span`
  min-width: ${({ theme }) => theme.spacing(2.5)};
  text-align: center;
  font-weight: 600;
  font-size: ${({ theme }) => theme.typography.body};
`;

export const RemoveButton = styled(Button).attrs({
  variant: "outline",
  size: "sm",
})`
  align-self: flex-start;
  color: ${({ theme }) => theme.colors.accent.danger};
  border-color: ${({ theme }) => theme.colors.border.destructive};

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.text.inverse};
    background: ${({ theme }) => theme.colors.accent.danger};
    border-color: ${({ theme }) => theme.colors.accent.danger};
  }
`;

export const Summary = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const TotalLabel = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h3};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const CheckoutButton = styled(Button)`
  min-width: ${({ theme }) => theme.spacing(20)};
`;

export const RecommendedSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2.5)};
  padding-top: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
`;

export const RecommendedTitle = styled.h2`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.typography.h2};
  color: ${({ theme }) => theme.colors.accent.primary};
`;

export const RecommendedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

export const RecommendedCard = styled(Card).attrs({
  elevation: "sm",
})`
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  cursor: pointer;
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-4px);
    }
  }
`;

export const RecommendedImage = styled.img`
  width: 100%;
  height: ${({ theme }) => theme.spacing(18)};
  object-fit: cover;
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
`;

/** Shown when a basket has no photo uploaded — keeps the card height stable. */
export const RecommendedImageFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${({ theme }) => theme.spacing(18)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 24px;
`;

export const RecommendedName = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const RecommendedPrice = styled.p`
  margin: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: ${({ theme }) => theme.typography.body};
`;

/* ── Swipe-to-delete ── */

export const SwipeWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.xl}px;
`;

export const SwipeDeleteAction = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.accent.danger};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 700;
  cursor: pointer;
`;

export const SwipeContent = styled.div<{ $offsetX: number; $swiping: boolean }>`
  position: relative;
  z-index: 1;
  transform: translateX(${({ $offsetX }) => $offsetX}px);
  transition: ${({ $swiping }) => ($swiping ? "none" : "transform 0.25s ease")};
`;

/* A cart may hold several farms, and each one packs and delivers its own
   baskets, so the cart is grouped the way checkout will split it. */
export const FarmGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};

  & + & {
    margin-top: ${({ theme }) => theme.spacing(3)};
  }
`;

export const FarmGroupHeader = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  padding-bottom: ${({ theme }) => theme.spacing(0.5)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
`;

export const FarmGroupName = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: ${({ theme }) => theme.typography.h3};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const FarmGroupTotal = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SplitNote = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-left: 4px solid ${({ theme }) => theme.colors.accent.primary};
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.small};
`;
